import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import {
  districts,
  states,
  stars,
  raasis,
  maritalStatusOptions,
  idProofTypes,
  panCardOptions,
  roleOptions,
} from "../utils/formDataUtils";
import { useMutation } from "@apollo/client";
import { EDIT_MEMBER_MUTATION } from "../graphql/mutations";
import { Snackbar, Alert } from "@mui/material";

const EditUserModal = ({ isOpen, onClose, user }) => {
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [editMember] = useMutation(EDIT_MEMBER_MUTATION);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const storedUserType = localStorage.getItem("user_type");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (isOpen && user) {
      setFormData(user);
      setFormErrors({});
    }
  }, [isOpen, user?.id]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;

      const updatedValue =
            name === "Pan_Number" || name === "ID_Proof_Number"
          ? value.toUpperCase()
          : value;


    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset the error for the field
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (formData.Pan_Card === "Yes" && !formData.Pan_Number?.trim()) {
      errors.Pan_Number = "PAN Number is required";
    }

    if (formData.ID_Proof_Type && !formData.ID_Proof_Number?.trim()) {
      errors.ID_Proof_Number = "ID Proof Number is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    window.scrollTo(0, 0);
    if (!validateForm()) return;

    try {
      const formattedDob = new Date(formData.DOB).toLocaleDateString("en-CA");
      const { data: mutationData } = await editMember({
        variables: {
          id: formData.id,
          First_Name: formData.First_Name,
          Last_Name: formData.Last_Name,
          Mobile_Number: formData.Mobile_Number,
          DOB: formattedDob,
          City: formData.City,
          District: formData.District,
          State: formData.State,
          Pincode: formData.Pincode,
          Email: formData.Email,
          Occupation: formData.Occupation,
          Role: formData.Role,
          Address: formData.Address,
          Marital_Status: formData.Marital_Status,
          ID_Proof_Type: formData.ID_Proof_Type,
          ID_Proof_Number: formData.ID_Proof_Number,
          Pan_Card: formData.Pan_Card,
          Pan_Number: formData.Pan_Number,
          Star: formData.Star,
          Raasi: formData.Raasi,
          Kootram: formData.Kootram,
          Kuladeivam: formData.Kuladeivam,
          Family_Details: formData.Family_Details,
        },
      });

      if (mutationData?.editMember) {
        setSnackbarMessage("Member updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        onClose();
      }
    } catch (error) {
      const apiMessage =
        error?.graphQLErrors?.[0]?.message || "Failed to update member.";
      setSnackbarMessage(apiMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const getFieldInput = (field) => {
    const dropdownMap = {
      District: districts,
      State: states,
      Star: stars,
      Raasi: raasis,
      Marital_Status: maritalStatusOptions,
      ID_Proof_Type: idProofTypes,
      Pan_Card: panCardOptions,
      Role: roleOptions,
    };

    const label =
      field === "id"
        ? "ID"
        : field === "Kootram"
          ? "Gothram"
          : field.replaceAll("_", " ");
    const isDropdown = Object.keys(dropdownMap).includes(field);
    const isReadOnly = field === "Mobile_Number" || field === "DOB";
    // Disable logic
      const isDisabled =
          field === "id" ||
          (field === "ID_Proof_Type" && !!formData.ID_Proof_Number) ||
          (field === "Pan_Card" && formData.Pan_Card === "Yes" && !!formData.Pan_Number);


      const isLockedTextField =
  (field === "ID_Proof_Number" && user?.ID_Proof_Number) ||
  (field === "Pan_Number" && user?.Pan_Number);



    return (
      <div key={field} className="flex flex-col space-y-1">
        <label
          htmlFor={field}
          className="text-sm font-medium text-gray-700 text-left"
        >
          {label}
        </label>

        {isDropdown ? (
          <select
            name={field}
            id={field}
            value={formData[field] || ""}
            onChange={handleOnChange}
            disabled={isDisabled}
           className={`mt-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
  isReadOnly || isDisabled || isLockedTextField ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""
} ${["Pan_Number", "ID_Proof_Number"].includes(field) ? "uppercase" : ""}`}

          >
            <option value="">Select {label}</option>
            {dropdownMap[field].map((option) => {
              const value = typeof option === "string" ? option : option.value;
              const labelText = typeof option === "string" ? option : option.label;
              return (
                <option key={value} value={value}>
                  {labelText}
                </option>
              );
            })}
          </select>

        ) : (
          <input
            type="text"
            name={field}
            id={field}
            value={formData[field] || ""}
            onChange={handleOnChange}
            readOnly={isReadOnly || isLockedTextField}
            disabled={isDisabled || isLockedTextField}
            className={`mt-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
              isReadOnly || isDisabled || isLockedTextField
                ? "bg-gray-100 cursor-not-allowed text-gray-500"
                : ""
            }`}
          />


        )}

        {formErrors[field] && (
          <p className="text-sm text-red-500 text-left pl-1">{formErrors[field]}</p>
        )}

      </div>
    );
  };



  const fields = [
    "id",
    "First_Name",
    "Last_Name",
    "Mobile_Number",
    "Email",
    "DOB",
    "Occupation",
    "Address",
    "City",
    "District",
    "State",
    "Pincode",
    "Marital_Status",
    "Family_Details",
    "ID_Proof_Type",
    "ID_Proof_Number",
    "Pan_Card",
    "Pan_Number",
    // "Role",  <-- remove it from static list
    "Star",
    "Raasi",
    "Kootram",
    "Kuladeivam",
  ];

  // Conditionally add "Role" field only for superuser
  const visibleFields = storedUserType === "superuser" ? [...fields, "Role"] : fields;


  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3 }}
            className="fixed top-0 right-0 w-full max-w-xl pt-24 h-full bg-white shadow-xl z-50 overflow-y-auto"
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold">Edit Member Profile</h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-800"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-5">
             {visibleFields.map((field) => {
              if (field === "Pan_Number" && formData.Pan_Card !== "Yes") {
                return null;
              }
              if (field === "ID_Proof_Number" && !formData.ID_Proof_Type) {
                return null;
              }
              return getFieldInput(field);
            })}
            </div>

           <div className="p-4 border-t flex justify-end gap-4">
            <button
              onClick={onClose}
              style={{ backgroundColor: '#E52029', borderRadius: '999px' }}
              className="px-4 py-2 text-white hover:opacity-90"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              style={{ backgroundColor: "green", borderRadius: '999px' }}
             className="px-4 py-2 text-white hover:opacity-90"
            >
              Save Changes
            </button>
          </div>

          </motion.div>
        )}
      </AnimatePresence>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditUserModal;
